import { getAuthToken } from './auth/tokenStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../config';

export interface ChildrenInfo {
  id: string;
  taskId: number;
  firstName: string;
  lastName: string;
  relationshipToUser: string;
  dateOfBirth: string;
  feet: number;
  inches: number;
  weight: number;
  eyeColor: string;
  hairColor: string;
  identifyingMarks: string;
  schoolName: string;
  schoolPhone: string;
  teacherName: string;
  medicalConditions: string;
  medications: string;
  email: string;
  phoneNumber: string;
  address: string;
  visibilityState: 'LOCKED' | 'UNLOCKED';
}

export interface CreateChildrenInfoData {
  taskId: number;
  firstName: string;
  lastName: string;
  relationshipToUser: string;
  dateOfBirth: string;
  feet: number;
  inches: number;
  weight: number;
  eyeColor: string;
  hairColor: string;
  identifyingMarks: string;
  schoolName: string;
  schoolPhone: string;
  teacherName: string;
  medicalConditions: string;
  medications: string;
  email: string;
  phoneNumber: string;
  address: string;
}

export interface UpdateChildrenInfoData extends CreateChildrenInfoData {
  visibilityState?: 'LOCKED' | 'UNLOCKED';
}

class ChildrenInfoService {
  private async getAuthToken(): Promise<string | null> {
    return await getAuthToken();
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    });

    return response;
  }

  async getChildrenInfo(taskId: number): Promise<ChildrenInfo[]> {
    const response = await this.makeRequest(`/api/children-info?taskId=${taskId}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch children info: ${response.status}`);
    }
    
    return await response.json();
  }

  async createChildrenInfo(data: CreateChildrenInfoData): Promise<{ id: string }> {
    const response = await this.makeRequest('/api/children-info', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to create children info: ${response.status}`);
    }

    const result = await response.json();

    // Handle the backend response format: { success: true, data: {...} }
    if (result && result.success && result.data) {
      return result.data;
    }

    // Fallback for direct response
    return result;
  }

  async updateChildrenInfo(id: string, data: UpdateChildrenInfoData): Promise<ChildrenInfo> {
    const response = await this.makeRequest(`/api/children-info/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to update children info: ${response.status}`);
    }

    const result = await response.json();

    // Handle the backend response format: { success: true, data: {...} }
    if (result && result.success && result.data) {
      return result.data;
    }

    // Fallback for direct response
    return result;
  }

  async deleteChildrenInfo(id: string): Promise<void> {
    const response = await this.makeRequest(`/api/children-info/${id}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error(`Failed to delete children info: ${response.status}`);
    }
  }

  async setVisibilityState(id: string, visibilityState: 'LOCKED' | 'UNLOCKED'): Promise<ChildrenInfo> {
    const response = await this.makeRequest(`/api/children-info/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ visibilityState }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to update visibility state: ${response.status}`);
    }

    const result = await response.json();

    // Handle the backend response format: { success: true, data: {...} }
    if (result && result.success && result.data) {
      return result.data;
    }

    // Fallback for direct response
    return result;
  }

  async markAsCompleted(taskId: number): Promise<boolean> {
    const response = await this.makeRequest(`/api/children-info/complete/${taskId}`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to mark children info as completed: ${response.status}`);
    }

    return await response.json();
  }

  async getAllChildrenInfo(targetUserId?: string): Promise<ChildrenInfo[]> {
    const params = targetUserId ? `?targetUserId=${targetUserId}` : '';
    const response = await this.makeRequest(`/api/children-info${params}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch all children info: ${response.status}`);
    }

    const result = await response.json();

    // Handle the backend response format: { success: true, data: [...] }
    if (result && result.success && Array.isArray(result.data)) {
      return result.data;
    }

    // Fallback for direct array response
    return Array.isArray(result) ? result : [];
  }

  async lockChildrenInfo(childrenId: string): Promise<void> {
    const response = await this.makeRequest(`/api/children-info/${childrenId}/lock`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to lock children info: ${response.status}`);
    }
  }

  async unlockChildrenInfo(childrenId: string, emails?: string[]): Promise<void> {
    const response = await this.makeRequest(`/api/children-info/${childrenId}/unlock`, {
      method: 'POST',
      body: JSON.stringify({ granteeEmails: emails || [] }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to unlock children info: ${response.status}`);
    }
  }

  async getChildrenAccessGrants(childrenId: string): Promise<any[]> {
    const response = await this.makeRequest(`/api/children-info/${childrenId}/access-grants`);

    if (!response.ok) {
      throw new Error(`Failed to fetch children access grants: ${response.status}`);
    }

    const result = await response.json();

    // Handle the backend response format: { success: true, data: [...] }
    if (result && result.success && Array.isArray(result.data)) {
      return result.data;
    }

    // Fallback for direct array response
    return Array.isArray(result) ? result : [];
  }
}

export const childrenInfoService = new ChildrenInfoService();

